'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { type Dispatch, type SetStateAction } from 'react';
import { type JSONContent } from '@tiptap/core';
import type { RunnerRequestControllerAdapter } from '../../adapter';
import {
  buildCreateTriggerPayload,
  hasPromptContent,
  type CreateTriggerPayload,
  type PromptContentNode,
} from '../../index.js';
export { hasPromptContent } from '../../index.js';
import type { PendingAttachmentDraft } from '../types/attachment';
import {
  isRunnerDispatchPolicySelected,
  type DispatchPolicySelectionSource,
} from '../lib/runnerDispatchPolicySelection';
import { type Daemon, type DaemonTrigger } from '../lib/api';
import { type RunnerDispatchPolicyEntityType } from '../lib/api';
import { type ActiveConcurrencyWarningReason } from '../lib/daemonTriggerConflict';

type DaemonTriggerModalMode = 'create' | 'continue';
type Translate = (key: string) => string;

const emptyDaemonPrompt: JSONContent = {
  type: 'doc',
  content: [{ type: 'paragraph' }],
};

export type OpenDaemonTriggerModalDetail = {
  mode?: DaemonTriggerModalMode;
  /**
   * 홈 카드처럼 호스트 선택과 다른 출처에서 열린 경우, 화면에 표시된 요청 대상.
   * 준비 전 클릭을 보관했다가 여는 경우, 호스트가 같은 프로젝트를 선택했을 때만
   * 실행해 카드와 요청 대상의 일치를 보장한다.
   */
  projectId?: string;
  sourceTrigger?: DaemonTrigger;
  prefillPrompt?: JSONContent;
  /**
   * 모달 오픈 시 초기 첨부로 채울 드래프트 목록. 데스크탑 캡처 자동 첨부 등에서 사용.
   * 모달이 닫히면 일반 첨부 워크플로와 동일하게 초기화된다.
   */
  prefillAttachmentDrafts?: PendingAttachmentDraft[];
  dispatchContext?: {
    entityType: RunnerDispatchPolicyEntityType;
    entityId: string;
    entityState: string;
    entityLabel?: string;
  };
};

type ControllerModalState = {
  reset: () => void;
  daemonTriggerSelectableConfigs: Array<{ id: string }>;
  compatibleAgentConfigs: Array<{ id: string }>;
  daemonAgentConfigId: string;
  setDaemonAgentConfigId: (value: string) => void;
  setDaemonRunnerType: (value: string) => void;
  setDaemonModel: (value: string) => void;
  setDaemonFastMode: (value: boolean) => void;
  setDaemonEffort: (value: string) => void;
  daemonId: string;
  setDaemonId: (value: string) => void;
  daemonPrompt: JSONContent | undefined;
  setDaemonPrompt: (value: JSONContent | undefined) => void;
  daemonRunnerType: string;
  daemonModel: string;
  daemonFastMode: boolean;
  daemonEffort: string;
  daemonPlanMode: boolean;
  setDaemonPlanMode: (value: boolean) => void;
  isSelectedDaemonPlanModeOnly: boolean;
  isSelectedAgentConfigPlanModeOnly: boolean;
  isPlanModeForced: boolean;
  daemonUseWorktree: boolean;
  setDaemonUseWorktree: (value: boolean) => void;
  worktreeSelectionMode: 'new' | 'existing';
  selectedExistingWorktreeId: string;
  selectedDiscoveredWorktreeId: string;
  daemonRepositoryId: string;
  daemonBaseBranch: string;
  activeConcurrencyWarningReason: ActiveConcurrencyWarningReason | null;
  isResolvedSettingsError: boolean;
  selectedUserConventionIds: string[];
  setSelectedUserConventionIds: (value: string[]) => void;
};

/**
 * 컨트롤러가 생성·이어쓰기 뮤테이션에 넘기는 입력. 패키지가 소유하고,
 * 호스트 어댑터 입력은 이 모양을 만족해야 한다.
 * (생성 본문은 buildCreateTriggerPayload 결과에 호스트 엔티티 enum을 덧씌운 값이다.)
 */
export type RunnerRequestCreateTriggerInput = Omit<CreateTriggerPayload, 'dispatchPolicy'> & {
  dispatchPolicy?: {
    policyId: string;
    entityType: RunnerDispatchPolicyEntityType;
    entityId: string;
    additionalPrompt: PromptContentNode;
  };
};

export type RunnerRequestContinueTriggerInput = {
  prompt: object;
  runnerType?: string;
  model?: string | null;
  fastMode?: boolean;
  effort?: string | null;
  userConventionIds?: string[];
  attachmentDrafts?: Array<{ key: string; originalName: string }>;
};

type UseDaemonTriggerModalControllerArgs = {
  token: string | null;
  selectedProjectId: string;
  selectedTeamId: string;
  isProjectResourcesLoading?: boolean;
  daemons: Daemon[];
  modalState: ControllerModalState;
  mode: DaemonTriggerModalMode;
  setMode: Dispatch<SetStateAction<DaemonTriggerModalMode>>;
  continueSourceTrigger: DaemonTrigger | null;
  setContinueSourceTrigger: Dispatch<SetStateAction<DaemonTrigger | null>>;
  t: Translate;
};

export const useRunnerRequestController = ({
  adapter,
  token,
  selectedProjectId,
  selectedTeamId,
  isProjectResourcesLoading = false,
  daemons,
  modalState,
  mode,
  setMode,
  continueSourceTrigger,
  setContinueSourceTrigger,
  t,
}: UseDaemonTriggerModalControllerArgs & {
  adapter: RunnerRequestControllerAdapter<
    RunnerRequestCreateTriggerInput,
    RunnerRequestContinueTriggerInput,
    OpenDaemonTriggerModalDetail
  >;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  // 오픈 직후 러너 리소스(공유 러너/에이전트/활성 트리거/컨벤션/레포)가 enabling 쿼리로
  // 로딩되는 동안에는 가용성 확인 중으로 보고 제출을 막는다. 더 이상 모달이 직접 refetch를
  // 발사하지 않고(중복 fetch 제거) 부모가 내려주는 로딩 상태에 위임한다.
  const isCheckingAvailability = isOpen && isProjectResourcesLoading;
  const [attachmentDrafts, setAttachmentDrafts] = useState<PendingAttachmentDraft[]>([]);
  const [dispatchContext, setDispatchContext] = useState<OpenDaemonTriggerModalDetail['dispatchContext']>();
  const [selectedDispatchPolicyId, setSelectedDispatchPolicyId] = useState<string | null>(null);
  // AUTO=추천/기본 자동 선택 추적, USER=사용자가 직접 변경(이후 refetch/추천 재계산이 덮어쓰지 않음).
  const [dispatchPolicySelectionSource, setDispatchPolicySelectionSource] =
    useState<DispatchPolicySelectionSource>('AUTO');

  const selectDispatchPolicyAsUser = useCallback((value: string | null) => {
    setSelectedDispatchPolicyId(value);
    setDispatchPolicySelectionSource('USER');
  }, []);

  const restoreRecommendedDispatchPolicy = useCallback((policyId: string) => {
    setSelectedDispatchPolicyId(policyId);
    setDispatchPolicySelectionSource('AUTO');
  }, []);
  const { createMutation, continueMutation, selection, feedback, subscribeOpen } = adapter;

  // 생성·이어쓰기 성공 뒤 결과 화면으로 이동한다. 이동 경로가 없는 호스트는
  // navigation을 공급하지 않으며, 그때는 모달을 닫는 것으로 끝난다.
  // 어댑터 반환은 공용 규약상 unknown이므로 실행 id만 방어적으로 꺼낸다.
  const openRunResult = adapter.navigation?.openRunResult;
  const navigateToRunResult = useCallback(
    (result: unknown) => {
      if (!openRunResult) return;
      const triggerId =
        typeof result === 'object' && result !== null && 'id' in result ? (result as { id: unknown }).id : undefined;
      if (typeof triggerId !== 'string' || triggerId.length === 0) return;
      // 홈의 URL은 모달 프로젝트 선택과 동기화되지 않으므로 제출 시점의 선택을 사용한다.
      openRunResult({ triggerId, teamId: selectedTeamId, projectId: selectedProjectId });
    },
    [openRunResult, selectedTeamId, selectedProjectId],
  );

  const isSubmittingRef = useRef(false);
  const closeModal = useCallback(() => {
    setIsOpen(false);
    setMode('create');
    setContinueSourceTrigger(null);
    modalState.reset();
    setAttachmentDrafts([]);
    setDispatchContext(undefined);
    setSelectedDispatchPolicyId(null);
    setDispatchPolicySelectionSource('AUTO');
  }, [modalState, setContinueSourceTrigger, setMode]);

  const handleClose = useCallback(() => {
    if (isSubmittingRef.current) {
      feedback.warning(t('submittingWaitNotice'));
      return;
    }
    closeModal();
  }, [closeModal, feedback, t]);

  // 준비 전 클릭 보존: token/프로젝트가 아직 없으면 의도를 버리지 않고 보관했다가
  // React 상태가 반영되면 한 번만 연다(홈 콜드 진입 첫 클릭 유실 방지).
  // detail이 없어도 빈 객체로 보관한다. 보관 값이 null일 때만 "보관 없음"이다.
  const pendingOpenRef = useRef<OpenDaemonTriggerModalDetail | null>(null);

  const openNow = useCallback(
    (detail?: OpenDaemonTriggerModalDetail) => {
      const nextMode = detail?.mode === 'continue' && detail.sourceTrigger ? 'continue' : 'create';
      const sourceTrigger = nextMode === 'continue' ? (detail?.sourceTrigger ?? null) : null;

      if (nextMode === 'continue' && sourceTrigger) {
        setContinueSourceTrigger(sourceTrigger);
        modalState.setDaemonAgentConfigId(sourceTrigger.agentConfigId);
        modalState.setDaemonRunnerType(sourceTrigger.runnerType);
        modalState.setDaemonModel(sourceTrigger.model ?? '');
        modalState.setDaemonFastMode(sourceTrigger.fastMode);
        // 원본 Effort를 프리필한다. 러너/모델을 바꾸면 useDaemonTriggerModal의 stale-effort effect가 초기화한다.
        modalState.setDaemonEffort(sourceTrigger.effort ?? '');
        modalState.setDaemonId(sourceTrigger.targetDaemonId ?? sourceTrigger.claimedByDaemonId ?? '');
        modalState.setSelectedUserConventionIds(sourceTrigger.userConventionIds ?? []);
        modalState.setDaemonPlanMode(sourceTrigger.planMode);
        modalState.setDaemonUseWorktree(sourceTrigger.useWorktree);
      } else {
        setContinueSourceTrigger(null);
        if (!modalState.daemonAgentConfigId && modalState.compatibleAgentConfigs.length > 0) {
          modalState.setDaemonAgentConfigId(modalState.compatibleAgentConfigs[0].id);
        }
        const savedSelection = selection.getRunnerSelection(selectedProjectId);
        modalState.setDaemonRunnerType(savedSelection?.runnerType ?? '');
        modalState.setDaemonModel(savedSelection?.model ?? '');
        modalState.setDaemonFastMode(false);
        modalState.setDaemonEffort('');
      }

      const pickDaemonId = (list: Daemon[], currentId: string): string => {
        if (list.length === 0) return '';
        if (nextMode === 'continue' && sourceTrigger) {
          const inheritedDaemonId = sourceTrigger.targetDaemonId ?? sourceTrigger.claimedByDaemonId ?? '';
          if (inheritedDaemonId.length > 0 && list.some((d) => d.id === inheritedDaemonId)) {
            return inheritedDaemonId;
          }
        }
        if (currentId.length > 0 && list.some((d) => d.id === currentId)) {
          return currentId;
        }
        return list[0].id;
      };

      // 캐시된 데몬 목록으로 초기 선택을 잡는다. 오픈 직후 enabling 쿼리로 목록이 갱신되면
      // useDaemonTriggerModal의 데몬 재선택 effect가 유효하지 않은 선택을 자동 교정하므로,
      // 여기서 별도의 refetch + 재선택을 돌리지 않는다(오픈당 중복 fetch 제거).
      const assignedDaemonId = pickDaemonId(daemons, modalState.daemonId);
      modalState.setDaemonId(assignedDaemonId);

      if (detail?.prefillPrompt) {
        modalState.setDaemonPrompt(detail.prefillPrompt);
      }
      // 새 요청 컨텍스트마다 선택과 선택 출처를 초기화한다(추천 추적 모드로 복귀).
      setDispatchContext(nextMode === 'create' ? detail?.dispatchContext : undefined);
      setSelectedDispatchPolicyId(null);
      setDispatchPolicySelectionSource('AUTO');

      if (detail?.prefillAttachmentDrafts && detail.prefillAttachmentDrafts.length > 0) {
        setAttachmentDrafts(detail.prefillAttachmentDrafts);
      }

      setMode(nextMode);
      setIsOpen(true);
    },
    [daemons, modalState, selectedProjectId, setContinueSourceTrigger, setMode, token, selection],
  );

  const handleOpen = useCallback(
    (detail?: OpenDaemonTriggerModalDetail) => {
      if (!token || !selectedProjectId) {
        pendingOpenRef.current = detail ?? {};
        return;
      }
      pendingOpenRef.current = null;
      openNow(detail);
    },
    [token, selectedProjectId, openNow],
  );

  useEffect(() => subscribeOpen(handleOpen), [handleOpen, subscribeOpen]);

  // 보관된 열기 의도가 있으면 준비 완료 시 한 번만 실행한다. 명시적 projectId가 있으면
  // 호스트가 같은 프로젝트를 선택했을 때만 열어, 다른 프로젝트의 리소스로 제출되는
  // 일을 막는다. hover/focus 프리워밍은 별도 이벤트라 여기서 열리지 않는다.
  useEffect(() => {
    if (!token || !selectedProjectId) return;
    const pending = pendingOpenRef.current;
    if (!pending) return;
    if (pending.projectId && pending.projectId !== selectedProjectId) return;
    pendingOpenRef.current = null;
    openNow(pending);
  }, [token, selectedProjectId, openNow]);

  const handleSubmit = useCallback(async () => {
    const daemonPrompt = modalState.daemonPrompt ?? emptyDaemonPrompt;
    const isPolicyBasedCreateRequest =
      mode === 'create' &&
      isRunnerDispatchPolicySelected({
        hasDispatchContext: dispatchContext !== undefined,
        selectedPolicyId: selectedDispatchPolicyId,
      });
    if (!hasPromptContent(daemonPrompt) && !isPolicyBasedCreateRequest) {
      return;
    }
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;

    try {
      if (mode === 'continue') {
        if (!continueSourceTrigger?.id) {
          return;
        }
        try {
          const overrides: {
            runnerType?: string;
            model?: string | null;
            fastMode?: boolean;
            effort?: string | null;
            userConventionIds?: string[];
          } = {};
          if (modalState.daemonRunnerType && modalState.daemonRunnerType !== continueSourceTrigger.runnerType) {
            overrides.runnerType = modalState.daemonRunnerType;
            overrides.model = modalState.daemonModel || null;
            overrides.fastMode = modalState.daemonFastMode;
          } else if (modalState.daemonModel !== (continueSourceTrigger.model ?? '')) {
            overrides.model = modalState.daemonModel || null;
            overrides.fastMode = modalState.daemonFastMode;
          } else if (modalState.daemonFastMode !== (continueSourceTrigger.fastMode ?? false)) {
            overrides.fastMode = modalState.daemonFastMode;
          }
          // Effort override: 러너/모델이 바뀌면(overrides.model 설정됨) 새 모델 기준으로 명시 전송하고,
          // 그대로면 값이 달라졌을 때만 전송한다(같으면 서버가 원본값을 상속).
          if (overrides.model !== undefined) {
            overrides.effort = modalState.daemonEffort || null;
          } else if (modalState.daemonEffort !== (continueSourceTrigger.effort ?? '')) {
            overrides.effort = modalState.daemonEffort || null;
          }
          const sourceUserConventionIds = continueSourceTrigger.userConventionIds ?? [];
          const sameUserConventions =
            sourceUserConventionIds.length === modalState.selectedUserConventionIds.length &&
            sourceUserConventionIds.every((id) => modalState.selectedUserConventionIds.includes(id));
          if (!sameUserConventions) {
            overrides.userConventionIds = modalState.selectedUserConventionIds;
          }
          const continued = await continueMutation.mutateAsync({
            prompt: daemonPrompt as object,
            ...overrides,
            ...(attachmentDrafts.length > 0
              ? {
                  attachmentDrafts: attachmentDrafts.map((draft) => ({
                    key: draft.key,
                    originalName: draft.originalName,
                  })),
                }
              : {}),
          });
          feedback.success(t('daemonTriggerContinueSent'));
          closeModal();
          navigateToRunResult(continued);
        } catch (error) {
          const message = feedback.resolveError(error, t('daemonTriggerContinueFailed'));
          if (message) feedback.error(message);
        }
        return;
      }

      const payload = buildCreateTriggerPayload({
        prompt: daemonPrompt,
        projectId: selectedProjectId,
        daemonId: modalState.daemonId,
        agentConfigId: modalState.daemonAgentConfigId,
        runnerType: modalState.daemonRunnerType,
        model: modalState.daemonModel,
        fastMode: modalState.daemonFastMode,
        effort: modalState.daemonEffort,
        planMode: modalState.daemonPlanMode,
        isPlanModeForced: modalState.isPlanModeForced,
        useWorktree: modalState.daemonUseWorktree,
        repositoryId: modalState.daemonRepositoryId,
        baseBranch: modalState.daemonBaseBranch,
        worktreeSelectionMode: modalState.worktreeSelectionMode,
        selectedDiscoveredWorktreeId: modalState.selectedDiscoveredWorktreeId,
        selectedExistingWorktreeId: modalState.selectedExistingWorktreeId,
        userConventionIds: modalState.selectedUserConventionIds,
        dispatchContext,
        selectedPolicyId: selectedDispatchPolicyId,
        attachmentDrafts,
        isProjectResourcesLoading,
        isAgentConfigCompatible: modalState.compatibleAgentConfigs.some(
          (config) => config.id === modalState.daemonAgentConfigId,
        ),
        isResolvedSettingsError: modalState.isResolvedSettingsError,
      });
      if (!payload) return;

      try {
        const { dispatchPolicy, ...requestPayload } = payload;
        const created = await createMutation.mutateAsync({
          ...requestPayload,
          // 호스트의 엔티티 enum을 보존한다. 정책 본문은 공용 빌더가 구성한다.
          ...(dispatchPolicy && dispatchContext
            ? { dispatchPolicy: { ...dispatchPolicy, entityType: dispatchContext.entityType } }
            : {}),
        });
        selection.saveRunnerSelection(selectedProjectId, modalState.daemonRunnerType, modalState.daemonModel);
        feedback.success(t('daemonTriggerSent'));
        closeModal();
        navigateToRunResult(created);
      } catch (error) {
        const message = feedback.resolveError(error, t('daemonTriggerFailed'));
        if (message) feedback.error(message);
      }
    } finally {
      isSubmittingRef.current = false;
    }
  }, [
    attachmentDrafts,
    feedback,
    selection,
    closeModal,
    continueMutation,
    continueSourceTrigger,
    createMutation,
    isProjectResourcesLoading,
    modalState,
    mode,
    selectedProjectId,
    dispatchContext,
    selectedDispatchPolicyId,
    navigateToRunResult,
    t,
  ]);

  const isSubmitPending = mode === 'continue' ? continueMutation.isPending : createMutation.isPending;

  return {
    isOpen,
    isCheckingAvailability,
    mode,
    continueSourceTrigger,
    handleOpen,
    handleClose,
    handleSubmit,
    isSubmitPending,
    attachmentDrafts,
    setAttachmentDrafts,
    dispatchContext,
    selectedDispatchPolicyId,
    setSelectedDispatchPolicyId,
    dispatchPolicySelectionSource,
    selectDispatchPolicyAsUser,
    restoreRecommendedDispatchPolicy,
  };
};
